import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toPng } from 'html-to-image';

import { Button, toast } from '@saas-kit/ui';

import type { KioskInfo } from './apiKiosk';
import { printKioskToken } from './apiKiosk';
import { KioskTokenTicket } from './KioskTokenTicket';
import { printReceipt } from './printReceipt';
import { QmsPrintPortal } from './QmsPrintPortal';
import { QmsTokenReceipt } from './QmsTokenReceipt';
import type { QmsBranding, QmsToken } from './types';

/** How long the issued token stays on the shared in-office kiosk before it
 *  resets for the next walk-in. A visitor on their own phone keeps the token
 *  until they dismiss it themselves — no auto-reset there. */
const OFFICE_RESET_MS = 45_000;

export type KioskMode = 'personal' | 'office';

/**
 * Full-screen confirmation shown after a walk-in self-issues a token.
 *
 * The ticket carries the centre's branding so it reads as an official
 * receipt. A visitor on their own phone (`personal`) saves it as an image
 * and keeps it on screen as long as they like; the in-office lobby kiosk
 * (`office`) prints it on the centre's printer — auto-printing once on mount
 * so a walk-in just collects the slip — then resets itself for the next one.
 */
export function KioskTokenView({
  info,
  token,
  serviceName,
  serviceNameAr,
  mode,
  kioskToken,
  onDone,
}: {
  info: KioskInfo;
  token: QmsToken;
  serviceName: string;
  serviceNameAr?: string | null;
  mode: KioskMode;
  /** Signed `t` token — scopes the print request to this kiosk's tenant. */
  kioskToken: string;
  onDone: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('qms');
  const ticketRef = useRef<HTMLDivElement>(null);
  const printed = useRef(false);
  const [saving, setSaving] = useState(false);

  // Only the shared office kiosk recycles itself; a personal phone does not.
  useEffect(() => {
    if (mode !== 'office') return;
    const timer = window.setTimeout(onDone, OFFICE_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [mode, onDone]);

  // Print the slip on the centre's printer: the backend renders the token
  // to ESC/POS and (with the `agent` transport) pushes it to the tenant's
  // on-LAN print agent. With the `agent` transport the backend owns
  // delivery, so an offline agent just gets a toast — no browser dialog.
  // Only the `noop` transport (or a failed call) falls back to the dialog.
  const handlePrint = useCallback(async (): Promise<void> => {
    try {
      const result = await printKioskToken(kioskToken, token.id);
      if (result.sent) {
        toast.success(t('print.toasts.sent'));
        return;
      }
      if (result.transport === 'agent') {
        toast.error(t('print.toasts.agent_offline'));
        return;
      }
    } catch {
      // Swallow — the dialog fallback below covers a failed call too.
    }
    printReceipt();
  }, [kioskToken, token.id, t]);

  // Office kiosk: print the slip once on mount so a walk-in needs no taps.
  useEffect(() => {
    if (mode !== 'office' || printed.current) return;
    printed.current = true;
    void handlePrint();
  }, [mode, handlePrint]);

  const handleSave = async (): Promise<void> => {
    const node = ticketRef.current;
    if (!node || saving) return;
    setSaving(true);
    try {
      // The logo is served same-origin via the API, so `toPng` inlines it
      // into the snapshot without a cross-origin storage CORS policy.
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      const link = document.createElement('a');
      link.download = `token-${token.display_code}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      toast.error(t('kiosk.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  // The kiosk's branding maps onto the same receipt the staff issue flow
  // prints, so an in-office slip is identical to a counter-issued one.
  const branding: QmsBranding = {
    logo_file_id: null,
    logo_url: info.logo_url,
    header_text: info.header_text,
    footer_text: info.footer_text,
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-7 bg-primary px-4 py-10 text-primary-foreground">
      <div className="overflow-hidden rounded-3xl shadow-2xl">
        <KioskTokenTicket
          ref={ticketRef}
          info={info}
          token={token}
          serviceName={serviceName}
          serviceNameAr={serviceNameAr ?? null}
          locale={i18n.language}
        />
      </div>
      <p className="text-sm text-primary-foreground/80">
        {t('kiosk.token_hint')}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {mode === 'office' ? (
          <Button
            onClick={() => void handlePrint()}
            className="px-8 py-3 text-base"
          >
            {t('kiosk.print')}
          </Button>
        ) : (
          <Button
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-8 py-3 text-base"
          >
            {t('kiosk.save')}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={onDone}
          className="px-8 py-3 text-base"
        >
          {t('kiosk.new_token')}
        </Button>
      </div>
      {mode === 'office' ? (
        <QmsPrintPortal>
          <QmsTokenReceipt
            token={token}
            serviceName={serviceName}
            serviceNameAr={serviceNameAr ?? null}
            branding={branding}
            locale={i18n.language}
          />
        </QmsPrintPortal>
      ) : null}
    </main>
  );
}
